import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PlaceOutlined } from "@mui/icons-material";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../redux/reducers/detailSidebarReducer";
import LoanHeaderChipList, { T } from "./LoanHeaderChipList";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";

/**
 * Subject properties in the loan drilldown header — chips + overflow popover
 * (see LoanHeaderChipList). Clicking a chip or a popover row opens the property
 * detail sidebar, the same one the Properties tab opens.
 */

const streetOnly = (property) => {
  const { streetNumber, streetName, street, fullAddress } =
    property?.address || {};
  if (streetNumber && streetName) return `${streetNumber} ${streetName}`;
  return (
    street ||
    (fullAddress ? fullAddress.split(",")[0].trim() : null) ||
    "No address"
  );
};

// Street + city only. Truncating at the data level (rather than with a CSS
// ellipsis) keeps chip widths predictable: state and zip buy nothing at this
// size, and the full address is one click away.
const shortAddress = (property) => {
  const city = property?.address?.city;
  const street = streetOnly(property);
  return [street, city].filter(Boolean).join(", ");
};

const normalize = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Order properties so the loan's primary/title property leads — otherwise the
 * header can open with a different address than the loan title right above it.
 * The loan name is the title property's address (e.g. "2457 Galaxy Ln"), so
 * match it against each property's street line; when nothing matches (unnamed
 * or renamed loan) the payload order stands, which is what the Properties tab
 * uses.
 */
const orderProperties = (properties, loanName) => {
  const list = [...(properties || [])];
  const target = normalize(loanName);
  if (!target) return list;
  const primaryIdx = list.findIndex((p) => {
    const street = normalize(streetOnly(p));
    const full = normalize(addressLabel(p?.address));
    if (!street) return false;
    return (
      target === street ||
      target.startsWith(street) ||
      street.startsWith(target) ||
      full.startsWith(target)
    );
  });
  if (primaryIdx <= 0) return list;
  const [primary] = list.splice(primaryIdx, 1);
  return [primary, ...list];
};

const LoanHeaderProperties = () => {
  const dispatch = useDispatch();
  const properties = useSelector(
    (state) => state.loanDrilldown?.subjectProperties,
  );
  const loanName = useSelector((state) => state.loanDrilldown?.loanName);

  const ordered = useMemo(
    () => orderProperties(properties, loanName),
    [properties, loanName],
  );

  const handleSelect = useCallback(
    (property) => {
      dispatch(
        detailSidebarOpen(
          DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY,
          property,
          property?._id,
          {
            title: addressLabel(property?.address) || shortAddress(property),
            mode: "view",
            source: "LoanHeaderProperties",
            sourceField: "subjectProperties",
          },
        ),
      );
    },
    [dispatch],
  );

  return (
    <LoanHeaderChipList
      items={ordered}
      getLabel={(count) => (count > 1 ? `Properties (${count})` : "Property")}
      getKey={(p) => p?._id || addressLabel(p?.address)}
      // Single-property loans (the common case) show the whole address — there
      // is nothing else competing for the space. Multi-property loans fall back
      // to street + city so the chips stay a predictable width.
      getChipLabel={(p, { isOnly } = {}) =>
        isOnly
          ? addressLabel(p?.address) || shortAddress(p)
          : shortAddress(p)
      }
      getTooltip={(p) => addressLabel(p?.address) || shortAddress(p)}
      getPrimary={streetOnly}
      getSecondary={(p) =>
        [p?.address?.city, p?.address?.state].filter(Boolean).join(", ")
      }
      getTrailing={(p) => p?.address?.zip || "—"}
      getSearchTerms={(p) => [
        addressLabel(p?.address),
        streetOnly(p),
        p?.address?.city,
        p?.address?.state,
        p?.address?.zip,
      ]}
      chipIcon={<PlaceOutlined sx={{ fontSize: 13, color: T.ink3 }} />}
      onSelect={handleSelect}
      filterPlaceholder="Filter by address or zip"
      viewAllPath="properties"
      viewAllLabel="View all in Properties tab"
    />
  );
};

export default LoanHeaderProperties;
